<template>
  <box-default>
    <div class="col-xs-12">
      <div class="default-box pi-page">
        <div class="pi-page__head">
          <h1 class="pi-page__title">Product Intelligence</h1>
          <pi-export-screen screen="Visão geral" :filters="exportFilters" :period="exportPeriodValue"/>
        </div>
        <pi-tabs current="overview"/>
        <pi-filters v-model="filters" :fields="filterFields" @clear="clearFilters"/>

        <pi-data-state :loading="loading" :error="error" :empty="isEmpty" :envelope="envelope" @retry="load(true)">
          <section class="pi-section" aria-labelledby="pi-overview-kpis">
            <div class="pi-section__head">
              <h2 id="pi-overview-kpis" class="pi-section__title">Indicadores do período</h2>
              <p class="pi-section__hint">{{ periodText }}</p>
              <pi-export-menu title="Indicadores do período" screen="Visão geral" :columns="kpiColumns" :rows="kpiRows" :envelope="envelope"
                              :filters="exportFilters" :period="exportPeriodValue" :disabled="!kpis.length"/>
            </div>
            <div class="pi-kpis">
              <pi-kpi-card v-for="kpi in kpis" :key="kpi.key" :label="kpi.label" :metric="kpi.metric"
                           :sparkline="kpi.sparkline" :to="kpi.route" :definition-key="kpi.definitionKey" :info-key="kpi.infoKey"
                           :definition-version="definitionVersion" :compare="compare"/>
            </div>
            <p v-if="!kpis.length" class="pi-overview__none">A API não devolveu indicadores para este recorte.</p>
          </section>

          <section class="pi-section" aria-labelledby="pi-overview-attention">
            <div class="pi-section__head">
              <h2 id="pi-overview-attention" class="pi-section__title">Atenção agora</h2>
              <pi-definition-tooltip info-key="overview.attention" label="Atenção agora"/>
              <p class="pi-section__hint">Regras determinísticas sobre os agregados; nenhuma conclusão é automática.</p>
              <pi-export-menu title="Atenção agora" screen="Visão geral" :columns="attentionColumns" :rows="attention" :envelope="envelope"
                              :filters="exportFilters" :period="exportPeriodValue" :disabled="!attention.length"/>
            </div>
            <pi-insight-list :insights="attention" empty-text="Nenhum insight aberto no período." company-links/>
          </section>

          <div class="pi-overview__split">
            <section class="pi-section pi-overview__half" aria-labelledby="pi-overview-concentration">
              <div class="pi-section__head">
                <h2 id="pi-overview-concentration" class="pi-section__title">Concentração: top 10 empresas por MAU</h2>
                <pi-definition-tooltip info-key="overview.concentration" :version="definitionVersion"/>
                <pi-export-menu title="Concentração (top 10)" screen="Visão geral" :columns="concentrationColumns" :rows="concentrationRows"
                                :envelope="envelope" :filters="exportFilters" :period="exportPeriodValue" :disabled="!concentrationRows.length"/>
              </div>
              <p class="pi-overview__share">
                <strong>{{ concentrationText }}</strong>
                <span v-if="concentrationRatio" class="pi-overview__muted"> · {{ concentrationRatio }}</span>
                <span v-if="concentrationBadge" class="pi-badge" :class="'pi-badge--' + concentrationTone">{{ concentrationBadge }}</span>
                <pi-definition-tooltip v-if="concentrationBadge && concentrationStateInfo" :info-key="concentrationStateInfo" :label="concentrationBadge"/>
              </p>
              <div v-if="concentrationRows.length" class="pi-table-wrap">
                <table class="pi-table">
                  <caption class="pi-sr">As 10 empresas com mais usuários ativos em 30 dias e a participação de cada uma no MAU</caption>
                  <thead>
                  <tr>
                    <pi-th v-for="column in concentrationHeader" :key="column.key" :column="column"/>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="row in concentrationRows" :key="row.key">
                    <td class="is-num pi-overview__rank">{{ row.rankText }}</td>
                    <td><pi-company-name :company-id="row.companyId" :shard-code="row.shardCode" :name="row.name"/></td>
                    <td class="is-num">
                      <span class="pi-overview__share-cell">
                        <span v-if="row.barPct !== null" class="pi-overview__track" aria-hidden="true">
                          <span class="pi-overview__fill" :style="{ width: row.barPct + '%' }"></span>
                        </span>
                        {{ row.shareText }}
                      </span>
                    </td>
                    <td class="is-num">{{ row.mauText }}</td>
                  </tr>
                  </tbody>
                </table>
              </div>
              <p v-else class="pi-overview__none">Sem empresas com uso no período.</p>
            </section>

            <section class="pi-section pi-overview__half" aria-labelledby="pi-overview-operations">
              <div class="pi-section__head">
                <h2 id="pi-overview-operations" class="pi-section__title">Sucesso das operações</h2>
                <router-link class="pi-link" :to="operationsRoute">Ver confiabilidade</router-link>
                <pi-export-menu title="Sucesso das operações" screen="Visão geral" :columns="operationColumns" :rows="operationItems"
                                :envelope="envelope" :filters="exportFilters" :period="exportPeriodValue" :disabled="!operations.length"/>
              </div>
              <div v-if="operations.length" class="pi-table-wrap">
                <table class="pi-table">
                  <caption class="pi-sr">Taxa de sucesso funcional por operação no período</caption>
                  <thead>
                  <tr>
                    <pi-th v-for="column in operationHeader" :key="column.key" :column="column"/>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="operation in operations" :key="operation.key">
                    <td>{{ operation.label }}</td>
                    <td class="is-num">
                      {{ operation.valueText }}
                      <span v-if="operation.ratioText" class="pi-table__sub">{{ operation.ratioText }}</span>
                    </td>
                    <td>
                      <span class="pi-badge" :class="'pi-badge--' + operation.tone" :title="operation.description">{{ operation.stateText }}</span>
                      <span v-if="operation.reason" class="pi-table__sub">{{ operation.reason }}</span>
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>
              <p v-else class="pi-overview__none">{{ operationsEmptyText }}</p>
            </section>
          </div>
        </pi-data-state>
      </div>
    </div>
  </box-default>
</template>

<script>
import boxDefault from "../../components/layouts/boxDefault.vue";
import piTabs from "../../components/pi/piTabs.vue";
import piFilters from "../../components/pi/piFilters.vue";
import piDataState from "../../components/pi/piDataState.vue";
import piKpiCard from "../../components/pi/piKpiCard.vue";
import piInsightList from "../../components/pi/piInsightList.vue";
import piDefinitionTooltip from "../../components/pi/piDefinitionTooltip.vue";
import piExportMenu from "../../components/pi/piExportMenu.vue";
import piExportScreen from "../../components/pi/piExportScreen.vue";
import piCompanyName from "../../components/pi/piCompanyName.vue";
import piTh from "../../components/pi/piTh.vue";
import { listFilterState } from "../../components/default/listFilterState";
import { describeError, piGet } from "../../components/pi/piApi.js";
import { screenColumns } from "../../components/pi/piColumns.js";
import { definitionFor, metricKeyFor } from "../../components/pi/piDefinitions.js";
import { exportPeriod, exportProvide, filterLines } from "../../components/pi/piExport.js";
import { formatInteger, formatPeriod, isNumber } from "../../components/pi/piFormat.js";
import { glossaryEntry, stateInfoKey } from "../../components/pi/glossary/index.js";
import { kpiLocation, viewPath } from "../../components/pi/piNavigation.js";
import {
  OVERVIEW_ATTENTION_COLUMNS, OVERVIEW_CONCENTRATION_COLUMNS, OVERVIEW_OPERATION_COLUMNS, concentrationRows, indicatorRows, kpiExportColumns,
  kpiInfoKey,
} from "../../components/pi/piOverviewModel.js";
import {
  PI_FILTER_DEFAULTS, PI_FILTER_KEY, buildApiQuery, compareEnabled, previousPeriod, resolvePeriod, toQueryString,
} from "../../components/pi/piQuery.js";
import { metricText, needsBadge, normalizeMetric, showsValue, stateDescription, stateLabel, stateTone, unavailableNotes } from "../../components/pi/piState.js";
import { PI_OVERVIEW } from "../../../../urlConfig";

const FILTER_FIELDS = ["period", "compare", "module", "roleClass", "platform", "sizeBand", "segment"];

const list = (value) => (Array.isArray(value) ? value.filter(Boolean) : []);

export default {
  name: "pi-overview",
  components: {
    boxDefault, piTabs, piFilters, piDataState, piKpiCard, piInsightList, piDefinitionTooltip, piExportMenu, piExportScreen, piCompanyName, piTh,
  },
  mixins: [listFilterState(PI_FILTER_KEY, PI_FILTER_DEFAULTS)],
  provide: exportProvide,
  data() {
    return {
      filterFields: FILTER_FIELDS,
      attentionColumns: OVERVIEW_ATTENTION_COLUMNS,
      concentrationColumns: OVERVIEW_CONCENTRATION_COLUMNS,
      concentrationHeader: screenColumns(OVERVIEW_CONCENTRATION_COLUMNS),
      operationColumns: OVERVIEW_OPERATION_COLUMNS,
      operationHeader: screenColumns(OVERVIEW_OPERATION_COLUMNS),
      loading: false,
      error: null,
      envelope: null,
      loadedKey: null,
      requestSeq: 0,
    };
  },
  computed: {
    apiParams() {
      return buildApiQuery(this.filters, { fields: FILTER_FIELDS });
    },
    apiKey() {
      return toQueryString(this.apiParams);
    },
    period() {
      return resolvePeriod(this.filters, new Date());
    },
    periodText() {
      return formatPeriod(this.period.from, this.period.to);
    },
    compare() {
      return compareEnabled(this.filters);
    },
    payload() {
      const payload = this.envelope && this.envelope.data;
      return payload && typeof payload === "object" ? payload : {};
    },
    definitionVersion() {
      return this.envelope ? this.envelope.definitionVersion : null;
    },
    kpis() {
      return list(this.payload.kpis).map((kpi, index) => {
        const definitionKey = (kpi.metric && kpi.metric.definitionKey) || metricKeyFor(kpi.key) || "";
        const definition = definitionFor(definitionKey);
        return {
          key: kpi.key || String(index),
          label: kpi.label || (definition && definition.name) || kpi.key || "Indicador",
          metric: kpi.metric || null,
          sparkline: list(kpi.sparkline),
          route: kpiLocation(kpi, this.$route.query, this.period) || "",
          definitionKey: definitionKey,
          infoKey: kpiInfoKey(kpi.key),
        };
      });
    },
    kpiColumns() {
      return kpiExportColumns(this.kpis);
    },
    kpiRows() {
      return indicatorRows(this.kpis, { period: this.period, previous: previousPeriod(this.period), compare: this.compare });
    },
    exportFilters() {
      return filterLines({ filters: this.filters, fields: FILTER_FIELDS });
    },
    exportPeriodValue() {
      return exportPeriod(this.filters);
    },
    attention() {
      return list(this.payload.attention);
    },
    concentration() {
      const value = this.payload.concentration;
      return value && typeof value === "object" ? value : {};
    },
    concentrationMetric() {
      return normalizeMetric(this.concentration.top10Share);
    },
    concentrationText() {
      return metricText(this.concentrationMetric) + " do MAU nas 10 maiores empresas";
    },
    concentrationRatio() {
      const metric = this.concentrationMetric;
      if (!isNumber(metric.numerator) || !isNumber(metric.denominator)) return "";
      return formatInteger(metric.numerator) + " de " + formatInteger(metric.denominator) + " usuários ativos no mês";
    },
    concentrationBadge() {
      const metric = this.concentrationMetric;
      return needsBadge(metric) && metric.value !== null ? stateLabel(metric.state) : "";
    },
    concentrationTone() {
      return stateTone(this.concentrationMetric.state);
    },
    concentrationStateInfo() {
      const key = stateInfoKey(this.concentrationMetric.state);
      return glossaryEntry(key) ? key : "";
    },
    concentrationRows() {
      return concentrationRows(this.concentration.companies);
    },
    operationItems() {
      return list(this.payload.operations);
    },
    operations() {
      return this.operationItems.map((operation, index) => {
        const metric = normalizeMetric(operation.successRate);
        const ratio = showsValue(metric) && isNumber(metric.numerator) && isNumber(metric.denominator)
          ? formatInteger(metric.numerator) + " de " + formatInteger(metric.denominator) + " tentativas"
          : "";
        return {
          key: operation.operationKey || String(index),
          label: operation.label || operation.operationKey || "Operação",
          valueText: metricText(metric),
          ratioText: ratio,
          stateText: stateLabel(metric.state),
          tone: stateTone(metric.state),
          description: stateDescription(metric.state, metric.stateReason),
          reason: metric.stateReason || "",
        };
      });
    },
    operationsEmptyText() {
      const note = unavailableNotes(this.envelope).filter((item) => /opera[cç]/i.test(item))[0];
      return note ? "n/d: " + note : "Nenhuma operação medida no período.";
    },
    operationsRoute() {
      return { path: viewPath("operations"), query: this.$route.query };
    },
    isEmpty() {
      if (!this.envelope) return false;
      return !this.kpis.length && !this.attention.length && !this.concentrationRows.length && !this.operations.length;
    },
  },
  watch: {
    apiKey() {
      this.load(false);
    },
  },
  created() {
    this.load(false);
  },
  methods: {
    load(force) {
      const key = this.apiKey;
      if (!force && key === this.loadedKey) return;
      this.loadedKey = key;
      this.requestSeq += 1;
      const seq = this.requestSeq;
      this.loading = true;
      this.error = null;
      piGet(PI_OVERVIEW, this.apiParams)
        .then((envelope) => {
          if (seq !== this.requestSeq) return;
          this.envelope = envelope;
        })
        .catch((error) => {
          if (seq !== this.requestSeq) return;
          this.envelope = null;
          this.error = describeError(error, "carregar a visão geral");
        })
        .then(() => {
          if (seq === this.requestSeq) this.loading = false;
        });
    },
  },
};
</script>

<style src="../../components/pi/pi.css"></style>

<style scoped>
.pi-page__head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px 16px;
  margin-bottom: 12px;
}

.pi-page__head .pi-page__title {
  margin: 0;
}

.pi-kpis {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(210px, 1fr));
  gap: 12px;
}

.pi-overview__split {
  display: flex;
  flex-wrap: wrap;
  gap: 0 28px;
}

.pi-overview__half {
  flex: 1 1 380px;
  min-width: 0;
}

.pi-overview__share {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin: 0 0 10px;
  font-size: 13px;
}

.pi-overview__muted {
  color: #6b7280;
}

.pi-overview__none {
  margin: 8px 0 0;
  font-size: 12px;
  color: #6b7280;
}

.pi-overview__rank {
  width: 1%;
  color: #6b7280;
}

.pi-overview__share-cell {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.pi-overview__track {
  display: inline-block;
  width: 56px;
  height: 6px;
  overflow: hidden;
  border-radius: 3px;
  background: #eceef1;
}

.pi-overview__fill {
  display: block;
  height: 100%;
  background: #1b59f8;
}

@media (max-width: 767px) {
  .pi-kpis {
    grid-template-columns: 1fr;
  }

  .pi-overview__half {
    flex-basis: 100%;
  }

  .pi-overview__track {
    display: none;
  }
}
</style>



// WEBPACK FOOTER //
// src/vue/pages/restricted/productIntelligence/piOverview.vue